import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdminDataModal, { FormField } from "../components/AdminDataModal";
import DataTable, { Column } from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import { useMessageModal } from "../contexts/MessageModalContext";

type TeamSection = "team_p1" | "team_p2";
type TeamStatus = "active" | "inactive";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  role2?: string;
  whatsapp?: string;
  image?: string;
  delay?: string;
  section: TeamSection;
  sort_order: number;
  status: TeamStatus;
  created_at?: string;
  updated_at?: string;
}

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const teamFields: FormField[] = [
  {
    key: "id",
    label: "ID",
    type: "text",
    disabled: true,
  },
  {
    key: "name",
    label: "Name",
    type: "text",
  },
  {
    key: "role",
    label: "Role",
    type: "text",
  },
  {
    key: "role2",
    label: "Role 2",
    type: "text",
  },
  {
    key: "section",
    label: "Section",
    type: "select",
    options: [
      { label: "Team P1", value: "team_p1" },
      { label: "Team P2", value: "team_p2" },
    ],
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    type: "text",
  },
  {
    key: "delay",
    label: "Animation Delay",
    type: "text",
  },
  {
    key: "sort_order",
    label: "Sort Order",
    type: "number",
  },
  {
    key: "image",
    label: "Image",
    type: "image",
    imageType: "single",
    imageFolder: "team",
    uploadField: "teamMemberImage",
  },
  {
    key: "status",
    label: "Status",
    type: "select",
    options: [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
  },
];

const AdminTeam: React.FC = () => {
  const { showModal } = useMessageModal();

  const [rows, setRows] = useState<TeamMember[]>([]);
  const [selectedRow, setSelectedRow] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const columns: Column[] = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "name", label: "Name" },
      { key: "role", label: "Role" },
      { key: "role2", label: "Role 2" },
      { key: "whatsapp", label: "WhatsApp" },
      {
        key: "image",
        label: "Image",
        imageFolder: "team",
      },
      {
        key: "section",
        label: "Section",
        render: (row: TeamMember) =>
          row.section === "team_p1" ? "Team P1" : "Team P2",
      },
      { key: "delay", label: "Delay" },
      { key: "sort_order", label: "Sort Order" },
      {
        key: "status",
        label: "Status",
        render: (row: TeamMember) => (
          <span
            className={`status-badge ${
              row.status === "active" ? "active" : "inactive"
            }`}
          >
            {row.status}
          </span>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/team`, {
        withCredentials: true,
      });
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(data);
    } catch (error) {
      console.error("Error fetching team members:", error);
      setRows([]);
      showModal({
        type: "error",
        title: "Failed",
        message: "Failed to load team members.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setActiveRow(null);
    setModalOpen(true);
  };

  const openViewModal = (row: TeamMember) => {
    setModalMode("view");
    setActiveRow(row);
    setModalOpen(true);
  };

  const openEditModal = (row: TeamMember) => {
    setModalMode("edit");
    setActiveRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveRow(null);
  };

  const handleSubmit = async (submittedData: any) => {
    try {
      const payload = new FormData();

      payload.append("name", submittedData.name || "");
      payload.append("role", submittedData.role || "");
      payload.append("role2", submittedData.role2 || "");
      payload.append("whatsapp", submittedData.whatsapp || "");
      payload.append("section", submittedData.section || "team_p1");
      payload.append("delay", submittedData.delay || "");
      payload.append("sort_order", String(submittedData.sort_order || 1));
      payload.append("status", submittedData.status || "active");

      if (submittedData.image?.file) {
        payload.append("teamMemberImage", submittedData.image.file);
      }

      if (submittedData.image?.removed) {
        payload.append("teamMemberImage", "REMOVE");
      }

      if (modalMode === "add") {
        await axios.post(`${API_URL}/admin/team`, payload, {
          withCredentials: true,
        });

        showModal({
          type: "success",
          title: "Success",
          message: "Team member created successfully.",
          confirmText: "OK",
        });
      } else {
        await axios.put(`${API_URL}/admin/team/${activeRow?.id}`, payload, {
          withCredentials: true,
        });

        showModal({
          type: "success",
          title: "Success",
          message: "Team member updated successfully.",
          confirmText: "OK",
        });
      }

      closeModal();
      fetchTeamMembers();
    } catch (error: any) {
      console.error("Error saving team member:", error);
      showModal({
        type: "error",
        title: "Failed",
        message:
          error?.response?.data?.message || "Failed to save team member.",
        confirmText: "OK",
      });
    }
  };

  const handleSoftDelete = async (row: TeamMember) => {
    showModal({
      type: "confirm",
      title: "Delete Team Member",
      message: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/admin/team/${row.id}`, {
            withCredentials: true,
          });

          showModal({
            type: "success",
            title: "Success",
            message: "Team member deleted successfully.",
            confirmText: "OK",
          });

          if (selectedRow?.id === row.id) {
            setSelectedRow(null);
          }

          fetchTeamMembers();
        } catch (error: any) {
          console.error("Error deleting team member:", error);
          showModal({
            type: "error",
            title: "Failed",
            message:
              error?.response?.data?.message || "Failed to delete team member.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <>
      <AdminListToolkit
        title="TEAM"
        columns={columns}
        data={rows}
        selectedRow={selectedRow}
        onAdd={openAddModal}
        onView={openViewModal}
        onEdit={openEditModal}
        onSoftDelete={handleSoftDelete}
        enableExport={true}
        exportFileName="team-members"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            loading={loading ? "Loading..." : ""}
            selectedRowId={selectedRow?.id || null}
            onRowSelect={(row) => setSelectedRow(row)}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Team Member"
        fields={teamFields}
        rowData={activeRow}
        onClose={closeModal}
        onSubmit={handleSubmit}
        size="lg"
      />
    </>
  );
};

export default AdminTeam;
